import { Checkmark, Copy, Edit } from '@drift-labs/icons';
import {
	AccountsTableItem,
	UseAccountsTable,
} from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/hooks/useAccountsTable';
import { abbreviateAddress, abbreviateAccountName } from 'src/utils/strings';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import React, { useState } from 'react';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { PublicKey } from '@solana/web3.js';
import { copyToClipboard } from 'src/utils/browser';

interface Props
	extends
		Pick<
			AccountsTableItem,
			| 'accountName'
			| 'authority'
			| 'pubKey'
			| 'userId'
			| 'isSuperStakeAccount'
			| 'isLpAccount'
			| 'isIsolatedAccount'
		>,
		Pick<UseAccountsTable, 'isDelegateVariant' | 'openEditAccountModal'> {}

export default function AccountsTableNameCell({
	accountName,
	authority,
	pubKey,
	userId,
	isSuperStakeAccount,
	isLpAccount,
	isIsolatedAccount,
	isDelegateVariant,
	openEditAccountModal,
}: Props) {
	const abbreviatedAccountName = abbreviateAccountName(accountName, 13, {
		ellipsisMiddle: true,
	});

	const [dataCopied, setCopiedData] = useState<PublicKey>();

	const handleCopyData = (data: PublicKey) => {
		if (dataCopied?.equals(data)) {
			return;
		}

		copyToClipboard(data.toString());

		setCopiedData(data);

		setTimeout(() => setCopiedData(undefined), 2000);
	};

	return (
		<>
			<Tooltip
				content={
					<>
						<p className="flex items-center gap-x-xs">
							{isDelegateVariant ? (
								<>
									{abbreviateAddress(authority.toBase58())}
									<IconButton
										size="sm"
										variant="tertiary"
										onClick={() => handleCopyData(authority)}
									>
										{dataCopied?.equals(authority) ? <Checkmark /> : <Copy />}
									</IconButton>
								</>
							) : (
								abbreviatedAccountName
							)}
						</p>
						<div className="flex items-center gap-x-xs">
							<span>User PubKey: {abbreviateAddress(pubKey.toBase58())}</span>
							<IconButton
								size="sm"
								variant="tertiary"
								onClick={() => handleCopyData(pubKey)}
							>
								{dataCopied?.equals(pubKey) ? <Checkmark /> : <Copy />}
							</IconButton>
						</div>
						<p>Account ID: {userId}</p>
					</>
				}
			>
				{isDelegateVariant
					? abbreviateAddress(authority.toBase58())
					: abbreviatedAccountName}
			</Tooltip>

			{isDelegateVariant && (
				<span className="typo-b4 text-text-secondary">
					{abbreviatedAccountName}
				</span>
			)}

			{!isDelegateVariant &&
				!isSuperStakeAccount &&
				!isLpAccount &&
				!isIsolatedAccount && (
					<IconButton
						size="sm"
						variant="tertiary"
						className="-m-sm mr-0 p-sm pr-0"
						onClick={() => openEditAccountModal(userId)}
						icon={Edit}
					/>
				)}
		</>
	);
}
